import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { WebformCreateJobFlowState } from '../../../Domain/Port/CreateJob/webform-create-job-flow.state';

/**
 * Defines the possible steps in the job creation flow.
 * Each step corresponds to a specific screen in the UI.
 */
export type Step = 'welcome' | 'share-your-location' | 'date-time' | 'contact-info' | 'job-info' | 'review' | 'success';
/**
 * Ordered array of steps in the job creation flow, defining the sequence of screens.
 * This array is used to determine the previous step when navigating backwards.
 */
const steps: Step[] = ['welcome', 'share-your-location', 'date-time', 'contact-info', 'job-info', 'review'];

/**
 * Maps each step to its corresponding URL path.
 * Used for navigation between different screens in the job creation flow.
 */
const stepUrlMap: Record<Step, string> = {
    welcome: '/welcome',
    'share-your-location': '/jobs/new/share-your-location',
    'date-time': '/jobs/new/date-time',
    'contact-info': '/jobs/new/contact-info',
    'job-info': '/jobs/new/info',
    review: '/jobs/new/review',
    success: '/jobs/new/success'
};

/**
 * Service responsible for navigating between different steps in the job creation flow.
 *
 * This service handles:
 * - Navigation between steps in the job creation flow
 * - Tracking whether navigation is from the review screen
 * - Providing URLs for previous steps
 * - Ensuring proper flow direction based on state
 */
@Injectable({
    providedIn: 'root'
})
export class CreateJobFlowNavigatorService {
    /** Angular Router for navigation between routes */
    private readonly router = inject(Router);

    /** State service that maintains job creation data across steps */
    private readonly state = inject(WebformCreateJobFlowState);

    /**
     * Navigates to a specific step from the review screen.
     * Sets a flag indicating that navigation originated from the review screen,
     * which will be used to return to the review screen after editing.
     *
     * @param step The step to navigate to
     * @returns Promise that resolves when navigation is complete
     */
    async navigateFromReviewScreen(step: Step): Promise<void> {
        await this.state.setFromReviewScreen(true);
        return this.navigateToStep(step);
    }

    /**
     * Navigates to a specific step in the job creation flow.
     * If navigation originated from the review screen, returns to the review screen instead.
     *
     * @param step The step to navigate to
     * @returns Promise that resolves when navigation is complete
     */
    async navigateTo(step: Step) {
        const isFromReviewScreen = await this.state.fromReviewScreen();
        if (isFromReviewScreen) {
            await this.state.setFromReviewScreen(false);
            return this.navigateToStep('review');
        }
        return this.navigateToStep(step);
    }

    /**
     * Gets the router link for the previous step in the flow.
     * Used for back navigation buttons.
     *
     * @param step The current step
     * @returns URL string for the previous step
     */
    async getPreviousStepRouterLink(step: Step): Promise<string> {
        const isFromReviewScreen = await this.state.fromReviewScreen();
        if (isFromReviewScreen) {
            return stepUrlMap['review'];
        }
        const previousStep = this.getPreviousStep(step);
        return stepUrlMap[previousStep];
    }

    /**
     * Navigates to the first step in the job creation flow.
     *
     * @returns Promise that resolves when navigation is complete
     */
    async navigateToStart(): Promise<void> {
        await this.navigateTo('welcome');
    }

    /**
     * Navigates back to the previous step in the flow.
     *
     * @param step
     */
    async navigateBack(step: Step): Promise<void> {
        const previousStep = this.getPreviousStep(step);
        await this.navigateTo(previousStep);
    }

    /**
     * Internal method that performs the actual navigation to a step.
     * Converts the step to a URL path and uses the Angular Router to navigate.
     *
     * @param step The step to navigate to
     * @returns Promise that resolves when navigation is complete
     * @throws Error if the step is invalid
     */
    private async navigateToStep(step: Step): Promise<void> {
        const path = stepUrlMap[step];
        if (!path) {
            throw new Error(`Invalid step: ${step}`);
        }
        await this.router.navigateByUrl(path);
    }

    /**
     * Gets the previous step in the flow based on the steps array order.
     *
     * @param step The current step
     * @returns The previous step
     */
    private getPreviousStep(step: Step): Step {
        const currentStepIndex = steps.indexOf(step);
        if (currentStepIndex === -1) {
            return 'welcome';
        }
        return steps[currentStepIndex - 1];
    }
}
